// web_components/wcConsentManageModal/wcConsentManageModal.ts

import { WebComponent_Base } from '../../WebComponent_Base';
import { renderProductHTML } from './wcConsentManageModal_Renderer';
import { addEventListener, removeEventListener } from '../../../utils/events';
import { applyFocusTrap } from '../../../utils/focusTrap';
import { ConsentUpdate, getConsentStatus } from '../../../consent/consentManager';
import { WebComponent_Utils } from '../../WebComponent_Utils';
import { dispatchConsentUpdate } from '../../../events/consentUpdateEvent';

/**
 * A custom web component for managing cookie consent settings.
 * Provides three toggle options: Essential (always on), Functional, and Marketing.
 */
export class WcConsentManageModal extends WebComponent_Base {
	public instanceId: string;
	private lastFocusedElement: Element | null = null;
	private handleKeydown!: (event: Event) => void;

	constructor() {
		super();
		this.instanceId = WebComponent_Utils.generateUniqueId();
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'WcConsentManageModal';
	}

	/**
	 * Retrieves data attributes.
	 * Called from the BaseComponent after stylesheets are attached.
	 */
	protected getComponentData(): void {}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {}

	/**
	 * Perform additional setup after initialization.
	 */
	protected afterInitialize() {
		this.manageFocus();
		// Check consent status and update checkboxes accordingly
		this.updateConsentStatuses();
	}

	/**
	 * Renders the component based on the provided `dataAttributes` and `instanceId`.
	 * Appends the generated product content directly to the shadow DOM, after the attached styles.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderProductHTML(this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Sets up event listeners for the modal.
	 * This includes handling the 'save preferences' and 'close modal' actions.
	 */
	protected attachListeners(): void {
		const buttons = ['wc-modal__close', 'wc-consent-manage-modal__save-btn'];

		// Add click event listener for both close and save buttons
		buttons.forEach((buttonId) => {
			const button = this.shadowRoot!.querySelector('.' + buttonId);
			if (button) {
				addEventListener(button, 'click', this.savePreferences.bind(this));
			}
		});

		// Add the keydown event listener for "Escape" key
		addEventListener(document, 'keydown', this.handleKeydown);
	}

	/**
	 * Applies/removes the necessary classes to the body with an active modal in view.
	 * @param {boolean} modalActive - the boolean to determine if the modal is active or not.
	 */
	updateModalActiveProperties(modalActive: boolean) {
		const body = document.querySelector('body') as HTMLElement;
		setTimeout(function () {
			if (modalActive && !body.classList.contains('modal-active')) {
				body.classList.add('modal-active');
			} else {
				/**
				 * Only kill the 'modal-active' body class if no other modal is
				 * active / in-view on the page (e.g. the voucher modal)
				 */
				if (!body.querySelector('.modal-active')) {
					body.classList.remove('modal-active');
				}
			}
		}, 1);
	}

	/**
	 * Saves the user preferences for Functional and Marketing cookies.
	 * It dispatches a custom event with the updated consent settings for listeners.
	 */
	savePreferences() {
		const functionalChecked = (
			this.shadowRoot!.getElementById(
				'wc-consent-manage-modal__functional-input',
			) as HTMLInputElement
		).checked;
		const marketingChecked = (
			this.shadowRoot!.getElementById(
				'wc-consent-manage-modal__marketing-input',
			) as HTMLInputElement
		).checked;

		const consentUpdates: ConsentUpdate[] = [
			{ consentType: 'essential', consentGiven: true },
			{ consentType: 'functional', consentGiven: functionalChecked },
			{ consentType: 'marketing', consentGiven: marketingChecked },
		];

		dispatchConsentUpdate(consentUpdates);
		this.closeModal();
	}

	/**
	 * Closes the modal and restores focus to the last focused element.
	 * Ensures logical focus flow after the modal is dismissed.
	 * Removes the keydown listener to avoid memory leaks.
	 * Also calls the function to remove the body 'modal-active' class.
	 */
	async closeModal() {
		this.remove();

		if (this.lastFocusedElement) {
			(this.lastFocusedElement as HTMLElement).focus();
		}

		await customElements.whenDefined('wc-age-gate-modal');
		const ageGateModal = document.querySelector('wc-age-gate-modal') as HTMLElement | null;

		if (ageGateModal) {
			const ageGateModalShadowRoot = ageGateModal.shadowRoot?.querySelector(
				'.wc-age-gate-modal',
			) as HTMLElement | null;

			if (ageGateModalShadowRoot) {
				ageGateModalShadowRoot.focus();
				applyFocusTrap(ageGateModalShadowRoot);
			}
		}

		if (!ageGateModal && !document.body.querySelector('.modal-active')) {
			// Remove body class if no other light DOM modal or the Age Gate modal are present
			document.body.classList.remove('modal-active');
		}

		// Remove the keydown event listener when the modal is closed
		removeEventListener(document, 'keydown', this.handleKeydown);
	}

	/**
	 * Manages focus within the modal to ensure accessibility.
	 * Uses `requestAnimationFrame` to delay focus setting until after rendering is complete.
	 */
	manageFocus() {
		this.lastFocusedElement = document.activeElement;

		requestAnimationFrame(() => {
			const modal = this.shadowRoot!.querySelector('.wc-consent-manage-modal') as HTMLElement;
			if (modal) {
				modal.focus();
				applyFocusTrap(modal, true);
			}
		});
	}

	/**
	 * Checks consent statuses and updates checkboxes accordingly.
	 * If essential consent is given, it checks the statuses of functional and marketing consents
	 * and updates the corresponding checkboxes.
	 * If essential consent is not given, it automatically enables the functional and marketing consents
	 * by checking their checkboxes.
	 */
	updateConsentStatuses() {
		const essentialConsent = getConsentStatus('essential');

		// Update the checkboxes based on the consent status
		const functionalCheckbox = this.shadowRoot!.getElementById(
			'wc-consent-manage-modal__functional-input',
		) as HTMLInputElement;
		const marketingCheckbox = this.shadowRoot!.getElementById(
			'wc-consent-manage-modal__marketing-input',
		) as HTMLInputElement;

		if (essentialConsent) {
			// If essential consent is given, fetch functional and marketing statuses
			const functionalConsent = getConsentStatus('functional');
			const marketingConsent = getConsentStatus('marketing');

			functionalCheckbox.checked = functionalConsent;
			marketingCheckbox.checked = marketingConsent;
		} else {
			// If essential consent is not given, check both functional and marketing checkboxes
			functionalCheckbox.checked = false;
			marketingCheckbox.checked = false;
		}
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-consent-manage-modal', WcConsentManageModal);
